// Hook for loading JsBarcode
// components/AutoFocusScanner.tsx
import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  TextFieldProps
} from '@mui/material';
import { QrCodeScanner, Clear } from '@mui/icons-material';

interface AutoFocusScannerProps extends Omit<TextFieldProps, 'onChange' | 'onKeyPress'> {
  value: string;
  onChange: (value: string) => void;
  onScan: (value: string) => void;
  autoFocus?: boolean;
  autoSelect?: boolean;
  clearAfterScan?: boolean;
  scannerIcon?: React.ReactNode;
  showClearButton?: boolean;
  focusDelay?: number;
}

export interface AutoFocusScannerRef {
  focus: () => void;
  select: () => void;
  clear: () => void;
}

const AutoFocusScanner = forwardRef<AutoFocusScannerRef, AutoFocusScannerProps>(({
  value,
  onChange,
  onScan,
  autoFocus = true,
  autoSelect = true,
  clearAfterScan = true,
  scannerIcon = <QrCodeScanner />,
  showClearButton = true,
  focusDelay = 300,
  ...textFieldProps
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    select: () => inputRef.current?.select(),
    clear: () => {
      onChange('');
      inputRef.current?.focus();
    }
  }));

  // Auto-focus when component mounts or when autoFocus changes
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
        if (autoSelect) {
          inputRef.current?.select();
        }
      }, focusDelay);

      return () => clearTimeout(timer);
    }
  }, [autoFocus, autoSelect, focusDelay]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && value.trim()) {
      onScan(value.trim());
      if (clearAfterScan) {
        onChange('');
      }
      // Re-focus after scan
      setTimeout(() => {
        inputRef.current?.focus();
        if (autoSelect) {
          inputRef.current?.select();
        }
      }, 100);
    }
  };

  const handleScanClick = () => {
    if (value.trim()) {
      onScan(value.trim());
      if (clearAfterScan) {
        onChange('');
      }
      inputRef.current?.focus();
    }
  };

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (autoSelect) {
      e.target.select();
    }
    textFieldProps.onFocus?.(e);
  };

  return (
    <TextField
      {...textFieldProps}
      variant="outlined"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyPress={handleKeyPress}
      onFocus={handleFocus}
      inputRef={inputRef}
      InputProps={{
        ...textFieldProps.InputProps,
        startAdornment: (
          <InputAdornment position="start">
            {scannerIcon}
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <div style={{ display: 'flex', gap: '4px' }}>
              {showClearButton && value && (
                <IconButton
                  size="small"
                  onClick={handleClear}
                  color="default"
                >
                  <Clear />
                </IconButton>
              )}
              <IconButton
                onClick={handleScanClick}
                disabled={!value.trim()}
                color="primary"
              >
                <QrCodeScanner />
              </IconButton>
            </div>
          </InputAdornment>
        )
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          '&.Mui-focused fieldset': {
            borderWidth: 2
          }
        },
        ...textFieldProps.sx
      }}
    />
  );
});

AutoFocusScanner.displayName = 'AutoFocusScanner';

export default AutoFocusScanner;